/* ==========================================================================
   MN — GLOBAL
   Sliders tactiles, tiroirs, onglets, marquee, barres de stock, accordeons.
   Vanilla JS, zero dependance.
   ========================================================================== */
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     Utilitaires
     ---------------------------------------------------------------------- */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const on = (el, ev, fn, opt) => el && el.addEventListener(ev, fn, opt);

  const MN = (window.MN = window.MN || {});

  MN.formatMoney = function (cents) {
    const fmt = (window.mnSettings && window.mnSettings.moneyFormat) || '{{amount_with_comma_separator}} €';
    const amount = (cents / 100);
    const withComma = amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const withPeriod = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDecComma = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const noDecPeriod = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecPeriod)
      .replace(/\{\{\s*amount_with_period_and_space_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'"))
      .replace(/\{\{\s*amount\s*\}\}/g, withPeriod);
  };

  /* focus() sans effet de bord de defilement.
     Un focus() nu demande au navigateur d'amener la cible dans le champ de
     vision : il fait alors defiler TOUS les ancetres defilables, y compris
     ceux en overflow:hidden, qui restent scrollables par programme. Comme le
     panneau du tiroir est encore a translateX(-100%) au moment de l'ouverture,
     le navigateur y appliquait un scrollLeft positif qui ne repartait jamais :
     l'entete et la liste se retrouvaient decales vers la gauche et rognes.
     preventScroll neutralise ce comportement. */
  MN.focusSansScroll = function (el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  };

  /* Remet a zero le defilement parasite d'un conteneur et de ses ancetres */
  MN.resetScroll = function (el) {
    if (!el) return;
    if (el.scrollLeft) el.scrollLeft = 0;
    let p = el.parentElement;
    while (p && p !== document.body) {
      if (p.scrollLeft) p.scrollLeft = 0;
      p = p.parentElement;
    }
  };

  MN.trapFocus = function (container, opener) {
    const isFocusable = (el) => {
      /* Un sous-panneau ferme reste dans le flux (visibility:hidden) : ses
         liens doivent sortir du cycle de tabulation, sinon Tab envoie le focus
         sur un element situe a translateX(100%) et le navigateur fait defiler
         le panneau lateralement. */
      if (el.closest('[inert]')) return false;
      const sub = el.closest('.mn-menu-drawer__sub');
      if (sub && !sub.classList.contains('is-open')) return false;
      return el.offsetParent !== null;
    };
    const list = () =>
      $$(
        'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])',
        container
      ).filter(isFocusable);

    const focusables = list();
    if (!focusables.length) return () => {};
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      /* Recalcule a chaque Tab : le contenu focusable change quand un
         sous-panneau s'ouvre ou se ferme. */
      const items = list();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); MN.focusSansScroll(last); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); MN.focusSansScroll(first); }
    };
    container.addEventListener('keydown', handler);
    MN.focusSansScroll(focusables[0]);
    MN.resetScroll(container);
    return () => {
      container.removeEventListener('keydown', handler);
      if (opener) MN.focusSansScroll(opener);
    };
  };

  /* ----------------------------------------------------------------------
     1. SLIDER TACTILE
     Defilement natif + snap. Les fleches et les points se synchronisent
     sur la position de scroll, ce qui evite toute logique de transform.
     ---------------------------------------------------------------------- */
  class MnSlider extends HTMLElement {
    connectedCallback() {
      this.viewport = $('.mn-slider__viewport', this);
      this.track = $('.mn-slider__track', this);
      if (!this.viewport || !this.track) return;

      this.slides = $$('.mn-slider__slide', this.track);
      this.prev = $('.mn-slider__nav--prev', this);
      this.next = $('.mn-slider__nav--next', this);
      this.dots = $$('.mn-slider__dot', this);

      this._onScroll = this._throttle(this.update.bind(this), 80);
      on(this.viewport, 'scroll', this._onScroll, { passive: true });
      on(window, 'resize', this._onScroll, { passive: true });

      on(this.prev, 'click', () => this.scrollByPage(-1));
      on(this.next, 'click', () => this.scrollByPage(1));
      this.dots.forEach((dot, i) => on(dot, 'click', () => this.goTo(i)));

      this._enableDrag();
      this.update();
    }

    _throttle(fn, wait) {
      let t = null;
      return (...a) => {
        if (t) return;
        t = setTimeout(() => { t = null; fn(...a); }, wait);
      };
    }

    _step() {
      const slide = this.slides[0];
      if (!slide) return this.viewport.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || 0) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    scrollByPage(dir) {
      const perPage = Math.max(1, Math.floor(this.viewport.clientWidth / this._step()));
      this.viewport.scrollBy({ left: dir * this._step() * perPage, behavior: 'smooth' });
    }

    goTo(index) {
      const slide = this.slides[index];
      if (slide) this.viewport.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;

      if (this.dots.length) {
        const active = Math.round(scrollLeft / this._step());
        this.dots.forEach((d, i) => d.setAttribute('aria-current', String(i === active)));
      }
    }

    /* Glisser-deposer a la souris (le tactile utilise le scroll natif) */
    _enableDrag() {
      let down = false, startX = 0, startScroll = 0, moved = false;
      const vp = this.viewport;

      on(vp, 'pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = false;
        startX = e.clientX;
        startScroll = vp.scrollLeft;
        vp.style.scrollSnapType = 'none';
      });
      on(vp, 'pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        vp.scrollLeft = startScroll - dx;
      });
      const end = () => {
        if (!down) return;
        down = false;
        vp.style.scrollSnapType = '';
        if (moved) {
          const block = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          vp.addEventListener('click', block, { capture: true, once: true });
        }
      };
      on(vp, 'pointerup', end);
      on(vp, 'pointerleave', end);
      on(vp, 'pointercancel', end);
    }
  }
  customElements.define('mn-slider', MnSlider);

  /* ----------------------------------------------------------------------
     2. HERO / CARROUSEL PLEINE LARGEUR AVEC AUTOPLAY
     ---------------------------------------------------------------------- */
  class MnCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-slide]', this);
      if (this.slides.length < 2) {
        $$('[data-carousel-nav]', this).forEach((b) => (b.hidden = true));
        return;
      }
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed || '5', 10) * 1000;

      on($('[data-carousel-nav="prev"]', this), 'click', () => this.go(this.index - 1));
      on($('[data-carousel-nav="next"]', this), 'click', () => this.go(this.index + 1));
      this.dots = $$('[data-carousel-dot]', this);
      this.dots.forEach((d, i) => on(d, 'click', () => this.go(i)));

      this._swipe();
      this.render();
      if (this.autoplay) this.start();

      on(this, 'mouseenter', () => this.stop());
      on(this, 'mouseleave', () => this.autoplay && this.start());
    }

    disconnectedCallback() { this.stop(); }

    start() { this.stop(); this.timer = setInterval(() => this.go(this.index + 1), this.speed); }
    stop() { if (this.timer) clearInterval(this.timer); }

    go(i) {
      const n = this.slides.length;
      this.index = ((i % n) + n) % n;
      this.render();
    }

    render() {
      this.slides.forEach((s, i) => {
        const active = i === this.index;
        s.classList.toggle('mn-is-hidden', !active);
        s.setAttribute('aria-hidden', String(!active));
      });
      if (this.dots) this.dots.forEach((d, i) => d.setAttribute('aria-current', String(i === this.index)));
    }

    _swipe() {
      let x0 = null;
      on(this, 'touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      on(this, 'touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
        x0 = null;
      }, { passive: true });
    }
  }
  customElements.define('mn-carousel', MnCarousel);

  /* ----------------------------------------------------------------------
     3. TIROIRS (menu, recherche, panier)
     Les tiroirs sont deplaces dans <body> pour sortir du contexte
     d'empilement du header sticky.
     ---------------------------------------------------------------------- */
  MN.drawers = {};

  class MnDrawer extends HTMLElement {
    connectedCallback() {
      this.id_ = this.dataset.drawer;

      /* Le deplacement dans <body> provoque un disconnect puis un reconnect :
         connectedCallback est rappele. Sans ce garde-fou, tous les ecouteurs
         etaient poses deux fois. */
      if (this.parentElement !== document.body) {
        /* Editeur de theme : une section re-rendue laisse derriere elle
           l'ancien tiroir, deja sorti du wrapper de section. On le retire
           avant d'ajouter le nouveau, sinon les tiroirs s'empilent. */
        $$('mn-drawer[data-drawer="' + this.id_ + '"]', document.body).forEach((old) => {
          if (old !== this && old.parentElement === document.body) old.remove();
        });
        document.body.appendChild(this);
        return;
      }

      if (this._bound) return;
      this._bound = true;
      MN.drawers[this.id_] = this;

      on($('.mn-drawer__overlay', this), 'click', () => this.close());
      $$('[data-drawer-close]', this).forEach((b) => on(b, 'click', () => this.close()));

      /* Echap ferme d'abord le sous-panneau ouvert, le tiroir ensuite */
      on(document, 'keydown', (e) => {
        if (e.key !== 'Escape' || !this.classList.contains('is-open')) return;
        const open = $('.mn-menu-drawer__sub.is-open', this);
        if (open) this.closeSub(open);
        else this.close();
      });

      /* Sous-panneaux de navigation niveau 2 */
      $$('[data-submenu-open]', this).forEach((btn) =>
        on(btn, 'click', (e) => {
          e.preventDefault();
          const panel = $('#' + btn.dataset.submenuOpen, this);
          if (!panel) return;
          /* Un seul sous-panneau ouvert a la fois */
          $$('.mn-menu-drawer__sub.is-open', this).forEach((other) => {
            if (other !== panel) this.closeSub(other);
          });
          panel.classList.add('is-open');
          panel.removeAttribute('inert');
          panel._opener = btn;
          btn.setAttribute('aria-expanded', 'true');
          /* Le premier niveau redemarre en haut a la prochaine ouverture */
          const root = $('.mn-menu-drawer__root', this);
          if (root) panel._rootScroll = root.scrollTop;
          const back = $('[data-submenu-close]', panel);
          if (back) MN.focusSansScroll(back);
          MN.resetScroll(panel);
        })
      );

      $$('[data-submenu-close]', this).forEach((btn) =>
        on(btn, 'click', () => {
          const panel = btn.closest('.mn-menu-drawer__sub');
          if (panel) this.closeSub(panel);
        })
      );
    }

    closeSub(panel) {
      panel.classList.remove('is-open');
      panel.setAttribute('inert', '');
      const scroll = $('.mn-menu-drawer__sub-scroll', panel);
      if (scroll) scroll.scrollTop = 0;
      if (panel._opener) {
        panel._opener.setAttribute('aria-expanded', 'false');
        MN.focusSansScroll(panel._opener);
        panel._opener = null;
      }
      MN.resetScroll(panel);
    }

    open(opener) {
      this.classList.add('is-open');
      document.body.classList.add('mn-no-scroll');
      this.setAttribute('aria-hidden', 'false');

      const panel = $('.mn-drawer__panel', this);
      const root = $('.mn-menu-drawer__root', this);
      /* Etat de depart garanti : jamais de decalage lateral herite,
         et le premier niveau repart en haut. */
      if (panel) { panel.scrollLeft = 0; panel.scrollTop = 0; }
      if (root) { root.scrollLeft = 0; root.scrollTop = 0; }

      this._release = MN.trapFocus(panel, opener);

      /* Filet de securite : si un navigateur applique malgre tout un
         defilement lateral pendant l'animation d'ouverture, on l'annule. */
      if (panel) {
        const clear = () => { panel.scrollLeft = 0; };
        requestAnimationFrame(clear);
        setTimeout(clear, 300);
      }

      this.dispatchEvent(new CustomEvent('mn:drawer:open', { bubbles: true }));
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('mn-no-scroll');
      this.setAttribute('aria-hidden', 'true');
      $$('.mn-menu-drawer__sub', this).forEach((p) => {
        p.classList.remove('is-open');
        p.setAttribute('inert', '');
        if (p._opener) p._opener.setAttribute('aria-expanded', 'false');
        p._opener = null;
      });
      $$('[data-submenu-open]', this).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (this._release) this._release();
      this.dispatchEvent(new CustomEvent('mn:drawer:close', { bubbles: true }));
    }
  }
  customElements.define('mn-drawer', MnDrawer);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    e.preventDefault();
    const drawer = MN.drawers[trigger.dataset.drawerOpen];
    if (drawer) drawer.open(trigger);
  });

  /* ----------------------------------------------------------------------
     4. ONGLETS
     ---------------------------------------------------------------------- */
  class MnTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.panels = $$('[role="tabpanel"]', this);
      this.tabs.forEach((tab, i) => {
        on(tab, 'click', () => this.select(i));
        on(tab, 'keydown', (e) => {
          if (e.key === 'ArrowRight') this.select((i + 1) % this.tabs.length);
          if (e.key === 'ArrowLeft') this.select((i - 1 + this.tabs.length) % this.tabs.length);
        });
      });
      this.select(0);
    }
    select(index) {
      this.tabs.forEach((t, i) => {
        t.setAttribute('aria-selected', String(i === index));
        t.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((p, i) => (p.hidden = i !== index));
      const active = this.tabs[index];
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }
  }
  customElements.define('mn-tabs', MnTabs);

  /* ----------------------------------------------------------------------
     5. MARQUEE — duplication du groupe pour une boucle sans couture
     ---------------------------------------------------------------------- */
  class MnMarquee extends HTMLElement {
    connectedCallback() {
      const track = $('.mn-marquee__track', this);
      const group = $('.mn-marquee__group', track);
      if (!track || !group) return;
      /* Le CSS translate de -50% : il faut exactement deux groupes. */
      if (track.children.length === 1) track.appendChild(group.cloneNode(true));
      this.hidden = false;
    }
  }
  customElements.define('mn-marquee', MnMarquee);

  /* ----------------------------------------------------------------------
     6. BARRE DE STOCK — animation a l'entree dans le viewport
     ---------------------------------------------------------------------- */
  const stressObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const bar = entry.target;
        const pct = Math.max(0, Math.min(100, parseFloat(bar.dataset.percentage || '0')));
        const fill = $('.mn-stressbar__fill', bar);
        if (fill) fill.style.width = pct + '%';
        bar.classList.toggle('mn-stressbar--warn', pct <= 40 && pct > 15);
        bar.classList.toggle('mn-stressbar--critical', pct <= 15);
        stressObserver.unobserve(bar);
      });
    },
    { threshold: 0.25 }
  );
  MN.initStressbars = (ctx) => $$('.mn-stressbar', ctx || document).forEach((b) => stressObserver.observe(b));

  /* ----------------------------------------------------------------------
     7. NOTATION — largeur de remplissage fractionnaire
     ---------------------------------------------------------------------- */
  MN.initRatings = (ctx) =>
    $$('.mn-rating[data-rating]', ctx || document).forEach((el) => {
      const value = parseFloat(el.dataset.rating || '0');
      const max = parseFloat(el.dataset.ratingMax || '5');
      const fill = $('.mn-rating__fill', el);
      if (fill) fill.style.setProperty('--mn-rating-fill', (value / max) * 100 + '%');
    });

  /* ----------------------------------------------------------------------
     8. HEADER STICKY — mesure de la hauteur reelle
     Deux variables distinctes pour eviter le double comptage :
     --mn-header-h = header seul, --mn-header-total = header + annonce.
     ---------------------------------------------------------------------- */
  MN.measureHeader = function () {
    const header = $('#shopify-section-header .mn-header-wrapper');
    const announcement = $('#shopify-section-announcement-bar');
    const h = header ? header.offsetHeight : 56;
    const a = announcement && getComputedStyle(announcement).position !== 'fixed' ? announcement.offsetHeight : 0;
    document.documentElement.style.setProperty('--mn-header-h', h + 'px');
    document.documentElement.style.setProperty('--mn-announcement-h', a + 'px');
    document.documentElement.style.setProperty('--mn-header-total', h + a + 'px');
  };

  /* ----------------------------------------------------------------------
     9. « Voir plus » generique
     ---------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-toggle-more]');
    if (!btn) return;
    const target = document.getElementById(btn.dataset.toggleMore);
    if (!target) return;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    target.hidden = expanded;
    if (btn.dataset.labelMore && btn.dataset.labelLess) {
      btn.textContent = expanded ? btn.dataset.labelMore : btn.dataset.labelLess;
    }
  });

  /* ----------------------------------------------------------------------
     Initialisation
     ---------------------------------------------------------------------- */
  function init(ctx) {
    MN.initStressbars(ctx);
    MN.initRatings(ctx);
    MN.measureHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  on(window, 'resize', MN.measureHeader, { passive: true });

  /* Rechargement d'une section dans l'editeur de theme */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', () => MN.measureHeader());

  MN.init = init;
})();
